import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePersonioSetupRows, useDataSyncStatus } from '@/api/queries/useDataSync';
import { dataSyncKeys } from '@/api/query-keys';
import type { PersonioSeed } from './useSeedOnOpen';
import { setupDepartmentDrafts, setupTeamDrafts, setupUserDrafts } from './utils/setupRowsToDrafts';

const SEEDED_STEPS = ['departments', 'users', 'teams'] as const;
type SeededStep = (typeof SEEDED_STEPS)[number];

/** Per step, the read it took and the read of its own "Aus Personio laden". */
interface SeedLedger {
  seeded: Partial<Record<SeededStep, number>>;
  own: Partial<Record<SeededStep, number>>;
}

/**
 * The data-sync snapshot (Personio) as seeds for the departments, users
 * and teams steps, each with the wizard's ledger. The ledger lives with the
 * drafts on the page and records which read each step took, so a table the
 * admin emptied is not refilled on re-entry, and a new read is due again.
 * A step's own load counts as seen by every step that took an earlier read,
 * so it refills no other table; a step that never took one seeds on open. A
 * step whose own load it has not taken yet takes the newer read as its own.
 * `unsupplied` lists the people the read cannot fill the users step with.
 * All of it holds only while Personio is linked: an unlinked source seeds
 * nothing and no table counts as loaded from it.
 */
export function usePersonioSeeds() {
  const { data: status } = useDataSyncStatus();
  const isLinked = !!status?.personio;
  const queryClient = useQueryClient();
  const { data: linkedRows, dataUpdatedAt } = usePersonioSetupRows();
  const setupRows = isLinked ? linkedRows : undefined;
  const departmentSeed = useMemo(
    () => setupRows && setupDepartmentDrafts(setupRows.departments),
    [setupRows]
  );
  const userSeed = useMemo(() => setupRows && setupUserDrafts(setupRows.users), [setupRows]);
  const teamSeed = useMemo(() => setupRows && setupTeamDrafts(setupRows), [setupRows]);
  const [ledger, setLedger] = useState<SeedLedger>({ seeded: {}, own: {} });
  const [loadedSteps, setLoadedSteps] = useState<Partial<Record<SeededStep, boolean>>>({});
  const seedFor = <TRows>(step: SeededStep, rows: TRows | undefined): PersonioSeed<TRows> => ({
    rows,
    seeded: ledger.seeded[step] === dataUpdatedAt,
    onSeeded: () => {
      setLedger((prev) => ({ ...prev, seeded: { ...prev.seeded, [step]: dataUpdatedAt } }));
      setLoadedSteps((prev) => ({ ...prev, [step]: true }));
    },
    loaded: isLinked && loadedSteps[step] === true,
    onReplaced: () => setLoadedSteps((prev) => ({ ...prev, [step]: false })),
    ownLoad: ledger.own[step] === dataUpdatedAt,
    onLoaded: () => {
      // Read at call time: the load fetched the rows after this render.
      const loadedRead = queryClient.getQueryState(dataSyncKeys.setupRows())?.dataUpdatedAt;
      setLedger(({ seeded, own }) => {
        const next: SeedLedger = { seeded: {}, own: {} };
        for (const other of SEEDED_STEPS) {
          const ownRead = own[other];
          const seenRead = seeded[other];
          // Reads only grow: an own load is pending while the step took an older read.
          const pendingOwnLoad =
            ownRead !== undefined && (seenRead === undefined || ownRead > seenRead);
          const takesAsOwn = other === step || pendingOwnLoad;
          next.own[other] = takesAsOwn ? loadedRead : own[other];
          if (other in seeded) next.seeded[other] = takesAsOwn ? seeded[other] : loadedRead;
        }
        return next;
      });
    }
  });

  return {
    departments: seedFor('departments', departmentSeed),
    users: seedFor('users', userSeed),
    teams: seedFor('teams', teamSeed),
    unsupplied: setupRows?.unsupplied
  };
}
